/**
 * ReplayTemplateDialog — create / edit a replay template
 * (variant-scoped). Extracted from the now-retired pages/Templates.tsx
 * so the Start (TestRunner) page can render the editor inline.
 *
 * See TestAuthoringTemplateDialog.tsx for the consolidation rationale.
 */
import { useEffect, useState } from 'react'
import { Info } from 'lucide-react'
import {
  Button,
  Badge,
  Label,
  Input,
  Select,
  Checkbox,
  Tooltip,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@/components/ui'
import { BrowserSelect, type BrowserOption } from '@/components/BrowserSelect'
import { BetweenTestsPauseRow } from '@/components/BetweenTestsPauseRow'
import { BetweenStepsPauseRow } from '@/components/BetweenStepsPauseRow'
import { replayTemplatesAPI, testPlansAPI, applicationsAPI } from '@/lib/api'
import { useAppContextOptional } from '@/hooks/useAppContext'
import { ScheduleFields, type SchedulePayload } from '@/components/ScheduleFields'
import { createSuiteSchedule, updateSuiteSchedule } from '@/lib/createSuiteSchedule'
import { TriggersSection } from '@/components/TriggersSection'
import { useToast } from '@/hooks/useToast'
import { toUserMessage, extractRawDetails } from '@/lib/userMessage'
import { useRunTemplateLaunch } from '@/hooks/useRunTemplateLaunch'
import { RunTemplateLaunchFooter } from '@/components/RunTemplateLaunchFooter'
import type { ReplayTemplate, RunTemplatePool, RunTemplateSettings, TestSuite } from '@/types'
import {
  DEFAULT_INTER_TEST_DELAY_MS,
  DEFAULT_PLAYWRIGHT_TIMEOUT_MS,
  DEFAULT_SLOW_MOTION_MS,
} from '@ottotester/shared-types'

export function ReplayTemplateDialog({ template, variantId, applicationId, suites, onClose, onSaved, launch, pool, presetSuiteId, schedulable, editingSchedule }: {
  template: ReplayTemplate | null
  variantId: string
  /** Needed to list the variant's tests for the Specific tests picker
   * (#2181 slice 3). A prop rather than context, matching the resume
   * dialog: reading context here would make every test that renders this
   * dialog need a provider, and 38 of them do not have one. */
  applicationId: string
  suites: TestSuite[]
  onClose: () => void
  onSaved: () => void
  /** Present when the dialog was opened by the Run button rather than from
   * a list (#2175 slice 2). Swaps the footer for Run / Save and run.
   * `settings` is what is on screen; passing it starts the run with those
   * values without saving, and omitting it starts the run with whatever is
   * saved. */
  launch?: (settings?: Record<string, unknown>) => Promise<void> | void
  /** Which pool a NEW template is created in (#2175 slices 3 and 4).
   * Ignored on edit — the pool is write-once, because a schedule or a CI
   * rule already points at the row and moving it would leave that
   * reference outside its own pool. Omitted means the manual pool, via
   * the schema default. */
  pool?: RunTemplatePool
  /** Open with this suite already chosen, instead of whatever the template
   * has saved (#2302). Set by the Run Suite button, which opens this dialog
   * aimed at the suite the operator pressed it on.
   *
   * INITIAL ONLY. It seeds the `whichTests` initializer below and nothing
   * watches it afterwards, because every caller mounts this dialog to open
   * it rather than keeping it mounted. A caller that opens the dialog for
   * one suite and then another must remount it — keying on the suite id is
   * enough — or it will keep showing the first. */
  presetSuiteId?: string
  /** Show the When tab so this run can be started now OR put on a schedule
   * (#2391). Set by a suite's Run Suite button. When set, Schedule saves the
   * on-screen settings into a scheduler-pool run-setting the new schedule
   * owns; a hand-run stays ephemeral. Off elsewhere (per-test run, editing a
   * template on Runs) — those cannot schedule. */
  schedulable?: boolean
  /** Editing an existing schedule from Runs (#2391). When set, `template` is
   * the schedule's own scheduler-pool run-setting, the When tab shows that
   * schedule's timetable (no Run now), and saving updates both the settings
   * and the timetable. */
  editingSchedule?: {
    _id: string
    frequency?: string
    cronExpression?: string
    timezone?: string
    startDate?: string
    endDate?: string
    daysOfWeek?: number[]
  }
}) {
  const toast = useToast()
  // Failed-test issues open in the repository picked in the app's own
  // settings, through the workspace's GitHub Issues connection. The app
  // comes from context, so no caller has to pass it.
  const failedTestRepositoryId = useAppContextOptional()?.apps.find((a) => a.id === applicationId)?.failedTestRepositoryId ?? null
  const issueTrackingConfigured = !!failedTestRepositoryId
  const failedTestRepoName = useRepositoryName(applicationId, failedTestRepositoryId)
  const [name, setName] = useState(() => {
    // A run or schedule aimed at a suite reads best named after that suite,
    // not the variant's default template (#2391) — so a schedule made here
    // shows up as "Smoke Tests", not the manual template's name.
    if (presetSuiteId && suites.some(s => s.id === presetSuiteId)) {
      return suites.find(s => s.id === presetSuiteId)?.name || template?.name || ''
    }
    return template?.name || ''
  })
  const [description, setDescription] = useState(template?.description || '')
  /** Which tests this run covers, as ONE choice (#2181 slice 3).
   *
   * `__all__`, a suite id, or `__pick__` for a hand-picked list. Underneath
   * they are two separate stored fields — `suiteId` and `testCaseIds` — and
   * the id list silently wins over the suite at run time. Two controls could
   * therefore disagree with nothing on screen saying which one ran, so there
   * is one, and saving writes whichever field the choice implies and clears
   * the other.
   *
   * A saved id list opens as `__pick__`; anything else falls back to the
   * suite, and an unknown suite falls back to all tests. */
  const [whichTests, setWhichTests] = useState(() => {
    // #2302 — a caller that opened this FOR a suite wins over the saved
    // selection, including a saved id list. The operator pressed Run on
    // that suite; showing them the template's own choice instead would be
    // answering a question they did not ask. Still validated against the
    // known suites, for the same reason a saved id is.
    if (presetSuiteId && suites.some(s => s.id === presetSuiteId)) return presetSuiteId
    if (template?.testCaseIds?.length) return '__pick__'
    const sid = template?.suiteId || '__all__'
    if (sid === '__all__') return sid
    return suites.some(s => s.id === sid) ? sid : '__all__'
  })
  const [pickedTestIds, setPickedTestIds] = useState<ReadonlySet<string>>(
    () => new Set(template?.testCaseIds ?? []),
  )
  const [testDataMode, setTestDataMode] = useState<'off' | 'single-row' | 'all-rows'>(template?.testDataMode || 'off')
  const [browser, setBrowser] = useState<BrowserOption>(template?.browser || 'chromium')
  const [healOnFailure, setHealOnFailure] = useState(template?.healOnFailure ?? false)
  const [playwrightTimeout, setPlaywrightTimeout] = useState((template?.playwrightTimeout ?? DEFAULT_PLAYWRIGHT_TIMEOUT_MS) / 1000)
  // #1076 — per-assertion polling timeout (`await expect(...)`).
  // Separate from the page (test-level) timeout above; the UI
  // exposes this knob in MILLISECONDS (matching Playwright's docs
  // vocabulary and the field's persisted unit) rather than seconds
  // — operators tuning expect() polling typically reason in
  // milliseconds (Playwright's docs, the trace report, and
  // surfacing all use ms). Coalesce to 5000ms for legacy templates
  // fetched before the field existed: the Mongoose schema-level
  // default reads back as 5000 on hydration, but lean() reads
  // skip defaults so the dialog must coalesce too for symmetry.
  // #1076 follow-up: surface in SECONDS to match the sibling Page
  // timeout row (operators reading "Assertion timeout: 5000" next to
  // "Page timeout: 10" couldn't tell whether the units were mismatched
  // or mistyped). The schema and Playwright API still live in ms; we
  // convert at I/O boundaries (init: ms→s, save: s→ms).
  //
  // `Math.round` on init handles legacy templates saved under the
  // prior 500ms step (e.g., 5500ms → 6s) — without it the input would
  // surface fractional values that don't match the new integer step,
  // and the operator sees an "off" display. First save normalises.
  const [assertionTimeout, setAssertionTimeout] = useState(Math.round((template?.assertionTimeout ?? 5000) / 1000))
  // Pacing (#1642), stored in ms; between tests is shown in seconds.
  // 0 = no pause. A new template starts with the shared defaults; an
  // existing one shows what it stores, and a row without the field reads 0.
  const [interTestDelaySec, setInterTestDelaySec] = useState(
    Math.round((template ? template.interTestDelayMs ?? 0 : DEFAULT_INTER_TEST_DELAY_MS) / 1000),
  )
  const [slowMotionMs, setSlowMotionMs] = useState(
    template ? template.slowMotionMs ?? 0 : DEFAULT_SLOW_MOTION_MS,
  )
  // Coerce persisted values to the known tri-state set before
  // hydrating the Select. The backend route updates ReplayTemplates
  // via `findOneAndUpdate` WITHOUT `runValidators: true` (see
  // replayTemplateRepository.updateOneByIdInWorkspace), so an out-
  // of-enum value could in principle be persisted via a malformed
  // PUT body and surface here — without this guard the Select would
  // render with a value that doesn't match any <option>, leaving the
  // control visibly empty. Belt-and-suspenders: the route should
  // also be tightened, but the dialog must not assume a clean read
  // either. ottotester#814 (Copilot review).
  const captureTriState = (v: unknown): 'off' | 'on-failure' | 'always' =>
    v === 'off' || v === 'on-failure' || v === 'always' ? v : 'on-failure'
  const [captureTrace, setCaptureTrace] = useState<'off' | 'on-failure' | 'always'>(captureTriState(template?.captureTrace))
  // ottotester#814: paralleling captureTrace. Default 'on-failure'
  // matches the previous always-on-failure screenshot behavior so the
  // dialog opens with the same effective settings legacy templates ran
  // under, without forcing a Mongo migration.
  const [captureScreenshot, setCaptureScreenshot] = useState<'off' | 'on-failure' | 'always'>(captureTriState(template?.captureScreenshot))
  // `headedMode` was removed in #994 — see the dispatcher comment for
  // why (Docker has no X server; the option was silently broken for
  // every customer's canonical execution path). The field is also
  // dropped from the schema + scrubbed from existing docs by a boot-
  // time backfill in `backfillDropHeadedMode.ts`.
  const [logLevel, setLogLevel] = useState<'info' | 'debug' | 'trace'>(template?.logLevel || 'info')
  const [createIssueOnFailure, setCreateIssueOnFailure] = useState(template?.createIssueOnFailure ?? false)
  // #1115 — GitHub Check Run reporter flag. Default false for all
  // templates (including pre-#1115 docs that lack the field). The field
  // is only meaningful when the workspace has a GitHub CI integration; the
  // runtime dispatch silently skips if none is present.
  const [postCheckRunOnFailure, setPostCheckRunOnFailure] = useState(template?.postCheckRunOnFailure ?? false)
  // Run-health reporting flags (#1009). Same shape + defaults as the
  // matching fields on the authoring template editor so the operator
  // doesn't have to remember which template type owns the signal.
  const [reportConsoleErrors, setReportConsoleErrors] = useState(template?.reportConsoleErrors ?? false)
  const [reportNetworkErrors, setReportNetworkErrors] = useState(template?.reportNetworkErrors ?? false)
  /** The variant's tests, for the picker. Fetched only when the picker is
   * open: everyone who runs everything pays nothing, which is most runs.
   * A failed read leaves the list empty, and the picker says so rather than
   * showing an empty box that looks like the variant has no tests. */
  const [availableTests, setAvailableTests] = useState<Array<{ id: string; name: string }>>([])
  const [loadingTests, setLoadingTests] = useState(false)
  const [testsError, setTestsError] = useState(false)
  useEffect(() => {
    if (whichTests !== '__pick__' || !applicationId || !variantId) return
    let cancelled = false
    setLoadingTests(true)
    setTestsError(false)
    void testPlansAPI.getTestCases(applicationId, variantId)
      .then(rows => {
        if (cancelled) return
        setAvailableTests(rows.map(r => ({ id: r.id, name: r.name })))
      })
      .catch(() => {
        // Kept apart from an empty list: both leave nothing to show, but
        // "this variant has no tests" is a fact about their app and would
        // be a lie when the read simply failed.
        if (cancelled) return
        setAvailableTests([])
        setTestsError(true)
      })
      .finally(() => { if (!cancelled) setLoadingTests(false) })
    return () => { cancelled = true }
  }, [whichTests, applicationId, variantId])

  const togglePickedTest = (id: string) =>
    setPickedTestIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const [saving, setSaving] = useState(false)

  // Unified Run/Schedule dialog (#2391). The When tab shows only when the
  // caller allows scheduling (a suite's Run Suite). Settings holds how the
  // suite runs; When picks run-now (ephemeral) or a schedule (its own copy).
  const [activeTab, setActiveTab] = useState<'settings' | 'when'>('settings')
  // Editing a schedule is always a schedule (no run-now); creating defaults to
  // run-now so the dialog behaves like today's Run Suite until you pick When.
  const [whenMode, setWhenMode] = useState<'now' | 'schedule'>(editingSchedule ? 'schedule' : 'now')
  const [schedulePayload, setSchedulePayload] = useState<SchedulePayload | null>(null)

  /** "Specific tests" with nothing ticked cannot be saved or run.
   *
   * It would not do nothing — it would run EVERYTHING. The two fields are
   * read in order at trigger time, and an empty id list falls through to
   * the suite, which this choice sets to all tests. So the screen would say
   * Specific tests and the run would cover the lot, with nothing to notice.
   * Blocking is better than quietly reverting to All tests, which is a
   * different silent answer to a choice the operator made. */
  const pickedNothing = whichTests === '__pick__' && pickedTestIds.size === 0

  /** Everything on screen, in the shape the template is stored in.
   *
   * Split out of `handleSave` (#2175 slice 2) because the Run button needs
   * the same object without writing it — one builder, so a field added to
   * the form cannot reach the save path and miss the run path. */
  function buildSettings(): RunTemplateSettings {
    return {
      name: name.trim(),
      description: description.trim() || undefined,
      // One control, two stored fields. Whichever the choice does not use is
      // cleared, so a later read cannot find a stale value from the other.
      suiteId: whichTests === '__pick__' ? '__all__' : whichTests,
      testCaseIds: whichTests === '__pick__' ? [...pickedTestIds] : [],
      testDataMode,
      browser,
      healOnFailure,
      playwrightTimeout: playwrightTimeout * 1000,
      // #1076 follow-up — both timeouts surface in seconds in the
      // UI and are converted to ms here for the schema. The
      // round-then-clamp normalises out-of-range typed values
      // that may not have been blurred before Save was clicked
      // (the onChange accepts any non-negative integer to keep
      // the "clear-then-type-larger" flow usable; final guard
      // belongs here).
      assertionTimeout: Math.min(Math.max(Math.round(assertionTimeout) || 5, 1), 60) * 1000,
      // #1642 — s→ms, clamped to [0, 30] seconds (0 = no pause).
      interTestDelayMs: Math.min(Math.max(Math.round(interTestDelaySec) || 0, 0), 30) * 1000,
      // #1642 Slice 2 — per-action slow-motion (ms), already in ms.
      slowMotionMs: Math.max(Math.round(slowMotionMs) || 0, 0),
      captureTrace,
      captureScreenshot,
      logLevel,
      createIssueOnFailure,
      postCheckRunOnFailure,
      reportConsoleErrors,
      reportNetworkErrors,
    }
  }

  /** Returns true when the write landed. The launch footer's Save and run
   * needs to know: a failed save must not be followed by a run against the
   * old values while the screen shows the new ones. */
  async function handleSave(): Promise<boolean> {
    if (!name.trim()) return false
    setSaving(true)
    try {
      const data = { variantId, ...buildSettings() }
      if (template) {
        await replayTemplatesAPI.update(template.id, data)
      } else {
        await replayTemplatesAPI.create({ ...data, ...(pool ? { pool } : {}) })
      }
      onSaved()
      return true
    } catch (err) {
      toast({
        title: 'Failed to save replay template',
        description: toUserMessage(
          err,
          "Couldn't save the replay template. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
      return false
    } finally {
      setSaving(false)
    }
  }

  /** Create a schedule for this suite (#2391): save the on-screen settings as
   * a scheduler-pool run-setting the schedule owns, then create the schedule
   * pointing at it. Per #2175 each schedule keeps its own settings copy, so
   * editing a hand-run never changes what runs overnight. On failure the
   * just-created run-setting is deleted; a failed cleanup is benign — the row
   * never runs on its own. */
  async function handleCreateSchedule() {
    if (!name.trim() || pickedNothing || !schedulePayload) return
    setSaving(true)
    try {
      await createSuiteSchedule({ variantId, settings: buildSettings(), schedule: schedulePayload })
      onSaved()
      onClose()
    } catch (err) {
      toast({
        title: 'Failed to create schedule',
        description: toUserMessage(
          err,
          "Couldn't create the schedule. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  /** Save edits to an existing schedule from Runs (#2391): write the settings
   * back to the run-setting the schedule owns, then update its timetable. */
  async function handleUpdateSchedule() {
    if (!editingSchedule || !template || !name.trim() || pickedNothing || !schedulePayload) return
    setSaving(true)
    try {
      await updateSuiteSchedule({
        scheduleId: editingSchedule._id,
        templateId: template.id,
        variantId,
        settings: buildSettings(),
        schedule: schedulePayload,
      })
      onSaved()
      onClose()
    } catch (err) {
      toast({
        title: 'Failed to save schedule',
        description: toUserMessage(
          err,
          "Couldn't save the schedule. Try again — if the problem continues, check your connection.",
        ),
        rawDetails: extractRawDetails(err) ?? undefined,
        variant: 'error',
      })
    } finally {
      setSaving(false)
    }
  }

  // Only wired when the dialog was opened by the Run button. `launch` is a
  // no-op stand-in otherwise so the hook's call order never changes with a
  // prop — the rules of hooks do not bend for an optional feature.
  const launcher = useRunTemplateLaunch({
    buildSettings,
    save: handleSave,
    launch: launch ?? (() => {}),
  })

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{editingSchedule ? 'Edit Schedule' : schedulable ? 'Run Suite' : template ? 'Edit Replay Template' : 'New Replay Template'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {schedulable && (
            <div className="flex gap-1 border-b border-border">
              {(['settings', 'when'] as const).map(t => (
                <button
                  key={t}
                  type="button"
                  onClick={() => setActiveTab(t)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${activeTab === t ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                >
                  {t === 'settings' ? 'Settings' : 'Schedule'}
                </button>
              ))}
            </div>
          )}
          {activeTab === 'settings' && (
          <div className="space-y-4">
          <div className="space-y-2">
            <Label>Name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nightly Smoke" />
          </div>
          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input value={description} onChange={e => setDescription(e.target.value)} placeholder="Brief description" />
          </div>
          <div className="space-y-2">
            <Label id="browser-select-label" className="text-xs">Browser</Label>
            <BrowserSelect value={browser} onChange={setBrowser} ariaLabelledby="browser-select-label" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs" htmlFor="which-tests">Which tests</Label>
              <Select
                id="which-tests"
                options={[
                  { value: '__all__', label: 'All tests' },
                  ...suites.map(s => ({ value: s.id, label: s.name })),
                  {
                    value: '__pick__',
                    // The closed control has to say WHICH, not just that a
                    // choice was made: "Specific tests…" reads the same for
                    // one test and for twenty, and reopening to find out is
                    // the thing this label exists to save.
                    label: pickedTestIds.size > 0
                      ? `${pickedTestIds.size} test${pickedTestIds.size === 1 ? '' : 's'} chosen`
                      : 'Specific tests…',
                  },
                ]}
                value={whichTests} onChange={e => setWhichTests(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Test data</Label>
              <Select
                options={[{ value: 'off', label: 'Off' }, { value: 'single-row', label: 'Single row' }, { value: 'all-rows', label: 'All rows' }]}
                value={testDataMode} onChange={e => setTestDataMode(e.target.value as any)} />
            </div>
          </div>
          {/* Only when specific tests are chosen, so the common case of
              running everything looks exactly as it did before. */}
          {whichTests === '__pick__' && (
            <div className="rounded-md border">
              <div className="flex items-center justify-between px-3 py-1.5 border-b bg-muted/40">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Choose tests
                </span>
                <span className="text-[11px] text-muted-foreground">
                  {loadingTests
                    ? 'Loading…'
                    : `${pickedTestIds.size} of ${availableTests.length} chosen`}
                </span>
              </div>
              {!loadingTests && testsError ? (
                <p className="px-3 py-3 text-xs text-destructive">
                  Couldn't load this variant's tests. Close and reopen to try again.
                </p>
              ) : !loadingTests && availableTests.length === 0 ? (
                <p className="px-3 py-3 text-xs text-muted-foreground">
                  This variant has no tests yet. Choose All tests, or create some first.
                </p>
              ) : (
                <div className="max-h-44 overflow-auto">
                  {availableTests.map(tc => (
                    <label key={tc.id} className="flex items-center gap-2 px-3 py-1.5 text-xs border-b last:border-b-0 cursor-pointer">
                      <Checkbox
                        checked={pickedTestIds.has(tc.id)}
                        onCheckedChange={() => togglePickedTest(tc.id)}
                      />
                      <span className="truncate">{tc.name}</span>
                    </label>
                  ))}
                </div>
              )}
              {pickedNothing && !loadingTests && !testsError && availableTests.length > 0 && (
                <p className="px-3 py-2 text-xs text-muted-foreground border-t">
                  Tick at least one test, or choose All tests.
                </p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5">
                Page timeout (sec)
                <Tooltip
                  content="Maximum total time a single test can run before Playwright aborts it. Tune up when end-to-end test scenarios legitimately need longer (long form fills, long-running async work). Default 10."
                  triggerLabel="What is the page timeout?"
                  align="start"
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Tooltip>
              </Label>
              <Input type="number" min={1} max={300} value={playwrightTimeout} onChange={e => setPlaywrightTimeout(parseInt(e.target.value) || DEFAULT_PLAYWRIGHT_TIMEOUT_MS / 1000)} className="w-24" />
            </div>
            {/* #1076 follow-up — per-assertion polling timeout, now
                in seconds to match the sibling Page timeout row above.
                Range [1, 60] seconds mirrors the prior [1000, 60000]
                ms spec; the dialog converts s→ms at save time. */}
            <div className="space-y-2">
              <Label className="text-xs flex items-center gap-1.5">
                Assertion timeout (sec)
                <Tooltip
                  content="Per-assertion polling timeout (await expect(...) calls). Tune up for slow-rendering / lazy-loading apps. Default 5."
                  triggerLabel="What is the assertion timeout?"
                  align="end"
                >
                  <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
                </Tooltip>
              </Label>
              <Input
                type="number"
                min={1}
                max={60}
                step={1}
                placeholder="5"
                value={assertionTimeout}
                onChange={e => {
                  // Accept any non-negative integer during typing —
                  // clamping to [1, 60] mid-type breaks the
                  // "clear-then-type-larger-number" flow (each
                  // intermediate value would snap to the min).
                  // The native `min` / `max` attrs surface UI
                  // validation; the final clamp lives on the save
                  // handler below so saved values are guaranteed
                  // in-range.
                  const parsed = parseInt(e.target.value, 10)
                  setAssertionTimeout(Number.isFinite(parsed) && parsed >= 0 ? parsed : 0)
                }}
                onBlur={() => {
                  // Snap to the allowed range on blur — operator
                  // sees the final committed value before saving.
                  setAssertionTimeout(prev => Math.min(Math.max(prev || 5, 1), 60))
                }}
                className="w-28"
              />
            </div>
            {/* #1642 — pacing controls grouped on their own row between the
                timeouts and capture settings; tooltips open inward (tests
                left→start, steps right→end) so they don't clip the dialog. */}
            <BetweenTestsPauseRow value={interTestDelaySec} onChange={setInterTestDelaySec} align="start" />
            <BetweenStepsPauseRow value={slowMotionMs} onChange={setSlowMotionMs} align="end" />
            <div className="space-y-2">
              <Label className="text-xs">Trace capture</Label>
              <Select
                options={[{ value: 'off', label: 'Off' }, { value: 'on-failure', label: 'On failure' }, { value: 'always', label: 'Always' }]}
                value={captureTrace} onChange={e => setCaptureTrace(e.target.value as any)} />
            </div>
            <div className="space-y-2">
              {/* ottotester#814: mirrors Trace capture row exactly. */}
              <Label className="text-xs">Screenshot capture</Label>
              <Select
                options={[{ value: 'off', label: 'Off' }, { value: 'on-failure', label: 'On failure' }, { value: 'always', label: 'Always' }]}
                value={captureScreenshot} onChange={e => setCaptureScreenshot(e.target.value as any)} />
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Log verbosity</Label>
            <Select
              options={[{ value: 'info', label: 'Info' }, { value: 'debug', label: 'Debug' }, { value: 'trace', label: 'Trace' }]}
              value={logLevel} onChange={e => setLogLevel(e.target.value as any)} />
          </div>
          <div className="space-y-2">
            <label className="flex items-center gap-2">
              <Checkbox
                checked={healOnFailure}
                onCheckedChange={setHealOnFailure}
              />
              <span className="text-sm">Heal on failure</span>
            </label>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={createIssueOnFailure}
                onCheckedChange={setCreateIssueOnFailure}
                disabled={!issueTrackingConfigured}
              />
              <span className={`text-sm ${!issueTrackingConfigured ? 'text-muted-foreground' : ''}`}>Create issue on failure</span>
              {issueTrackingConfigured && (
                <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 ml-1">
                  GitHub{failedTestRepoName ? <> &middot; {failedTestRepoName}</> : null}
                </Badge>
              )}
            </label>
            {!issueTrackingConfigured && (
              <p className="text-xs text-muted-foreground pl-6">
                Pick a repository in the app's settings first, under "When a test fails, open a GitHub issue in".
              </p>
            )}
            {/* #1115 — GitHub Check Run reporter. Enabled unconditionally;
                the runtime dispatch guards on the workspace having a
                connected GitHub integration + buildMetadata carrying a
                commitSha. The Tooltip explains what's required so the
                operator knows what to set up.
                #1165 — label shortened to plain English (was
                "Post Check Run on failure to linked GitHub
                integration", which read as dev-jargon to the testers
                actually using the dialog); tooltip align flipped to
                `start` so the popover extends INTO the dialog body
                instead of toward the dialog's edge — combined with
                the shorter label this keeps the tooltip inside the
                viewport on small screens. The Tooltip primitive has
                no flip-on-collision today; if a second use site needs
                one, promote at that time. */}
            <label className="flex items-center gap-2">
              <Checkbox
                checked={postCheckRunOnFailure}
                onCheckedChange={setPostCheckRunOnFailure}
              />
              <span className="text-sm">Notify GitHub on failure (Check Run)</span>
              <Tooltip
                content="Posts a Check Run result to your workspace's GitHub connection whenever this template's runs fail. Requires a GitHub connection in Workspace Settings → Integrations, plus a trigger that carries the commit SHA and repo."
                triggerLabel="What is a GitHub Check Run?"
                align="start"
              >
                <Info className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
              </Tooltip>
            </label>
          </div>

          {/* Issue reporting section (#1009 of meta #1008). Twin
              toggles gating the run-health UI surfaces per-source.
              Both default ON. Same field names + same shape as the
              authoring template editor so the operator's mental
              model is symmetric across both template surfaces. */}
          <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Issue reporting</Label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportConsoleErrors} onCheckedChange={setReportConsoleErrors} />
              <span className="text-sm leading-snug">
                Report console errors
                <span className="block text-xs text-muted-foreground">
                  Surface uncaught <code>pageError</code>, <code>console.error</code>, and <code>console.warn</code> captured during runs from this template.
                </span>
              </span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox checked={reportNetworkErrors} onCheckedChange={setReportNetworkErrors} />
              <span className="text-sm leading-snug">
                Report network errors
                <span className="block text-xs text-muted-foreground">
                  Surface HTTP 4xx / 5xx and failed network requests captured during runs from this template.
                </span>
              </span>
            </label>
          </div>
          {/* Triggers section (#1134 Slice 1 of meta #1132).
              Only Replay templates get this section (Q7, ADR 0018).
              Not rendered when `template` is null (new template being
              created) — the rule must point at an existing templateId. */}
          {template && (
            <TriggersSection templateId={template.id} templateName={template.name} />
          )}
          </div>
          )}
          {schedulable && activeTab === 'when' && (
            <div className="space-y-3">
              {/* Editing an existing schedule is always a schedule — no
                  run-now choice; just its timetable, seeded (#2391). */}
              {!editingSchedule && (
                <>
                  <button
                    type="button"
                    onClick={() => setWhenMode('now')}
                    className={`w-full text-left rounded-md border p-3 transition-colors ${whenMode === 'now' ? 'border-primary bg-muted/40' : 'border-border hover:bg-muted/20'}`}
                  >
                    <div className="font-medium text-sm">Run now</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Run once, right now, with the settings on the Settings tab. Nothing is saved.</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setWhenMode('schedule')}
                    className={`w-full text-left rounded-md border p-3 transition-colors ${whenMode === 'schedule' ? 'border-primary bg-muted/40' : 'border-border hover:bg-muted/20'}`}
                  >
                    <div className="font-medium text-sm">Schedule for later</div>
                    <div className="text-xs text-muted-foreground mt-0.5">Run on a repeating timetable. Saves these settings as the schedule's own copy.</div>
                  </button>
                </>
              )}
              {/* The timetable is always mounted so the popup keeps ONE height
                  whether Run now or Schedule is highlighted (#2391 follow-up):
                  dimmed under Run now, active when Schedule (or when editing an
                  existing schedule). `inert` (not just pointer-events) takes it
                  out of the tab order and the a11y tree while it's inactive, so
                  keyboard users can't focus or edit it under Run now. */}
              <div
                className={`pt-1 ${(!editingSchedule && whenMode === 'now') ? 'opacity-50' : ''}`}
                inert={!editingSchedule && whenMode === 'now'}
              >
                <ScheduleFields
                  initial={editingSchedule ? {
                    frequency: editingSchedule.frequency as any,
                    cronExpression: editingSchedule.cronExpression,
                    timezone: editingSchedule.timezone,
                    startDate: editingSchedule.startDate,
                    endDate: editingSchedule.endDate,
                    daysOfWeek: editingSchedule.daysOfWeek,
                  } : undefined}
                  onChange={setSchedulePayload}
                />
              </div>
            </div>
          )}
        </DialogBody>
        {schedulable ? (
          // Run now keeps today's footer (Run / Save and run) unchanged; only
          // the Schedule mode swaps in a Create Schedule button (#2391).
          whenMode === 'now' ? (
            <RunTemplateLaunchFooter
              busy={saving || launcher.busy}
              canRun={!!name.trim() && !pickedNothing}
              onCancel={onClose}
              onRun={() => { void launcher.runNow() }}
              onSaveAndRun={() => { void launcher.saveAndRun() }}
            />
          ) : (
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => { void (editingSchedule ? handleUpdateSchedule() : handleCreateSchedule()) }}
                disabled={saving || !name.trim() || pickedNothing || !schedulePayload}
              >
                {saving ? 'Saving…' : editingSchedule ? 'Update Schedule' : 'Create Schedule'}
              </Button>
            </DialogFooter>
          )
        ) : launch ? (
          <RunTemplateLaunchFooter
            busy={saving || launcher.busy}
            canRun={!!name.trim() && !pickedNothing}
            onCancel={onClose}
            onRun={() => { void launcher.runNow() }}
            onSaveAndRun={() => { void launcher.saveAndRun() }}
          />
        ) : (
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={!name.trim() || saving || pickedNothing}>
              {saving ? 'Saving...' : template ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  )
}

/** The picked repository's current name, for the badge beside "Create
 * issue on failure". Best effort: the badge shows "GitHub" alone until
 * this loads, or if it can't. */
function useRepositoryName(applicationId: string, repositoryId: string | null): string | null {
  const [name, setName] = useState<string | null>(null)
  useEffect(() => {
    setName(null)
    if (!repositoryId) return
    let cancelled = false
    void (async () => {
      try {
        const options = await applicationsAPI.getFailedTestRepositoryOptions(applicationId)
        if (!cancelled) setName(options?.repos.find((r) => r.key === repositoryId)?.name ?? null)
      } catch {
        // The badge falls back to "GitHub".
      }
    })()
    return () => { cancelled = true }
  }, [applicationId, repositoryId])
  return name
}
